'use client';

import { useQueryClient } from '@tanstack/react-query';
import { AccountApi } from 'api/ApiHelper';
import axios, { AxiosError } from 'axios';
import { QueryKey } from 'constants/reactQuery';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { DateTime, Duration } from 'luxon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { fetchAccountMeQuery } from 'queries/account/useGetAccountMeQuery';
import { useCallback, useEffect, useState } from 'react';
import { useClient } from 'ui-hooks/useClient';
import { configure } from 'utils/configure';
import { showToast } from 'utils/modal';

export default function PartnerSignInForm() {
  const router = useRouter();

  const [tokenRemainingTime, setTokenRemainingTime] = useState('00:00');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [tokenState, setTokenState] = useState({
    challengeId: '',
    expiresAt: 0,
    error: '',
  });
  const queryClient = useQueryClient();
  const client = useClient();

  const handleSendToken = useCallback(async () => {
    try {
      const res = await AccountApi.sendSignInVerificationToken({
        email,
      });
      if (res.challengeId) {
        setTokenState({
          challengeId: res.challengeId,
          expiresAt: res.expiresAt ?? 0,
          error: '',
        });
        showToast('일회용 코드를 등록된 메일로 보냈어요.');
      } else {
        showToast('일회용 코드를 보내는데 실패했어요.');
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const data = (
          e as AxiosError<{
            message?: string;
          }>
        ).response?.data;

        setTokenState({
          challengeId: '',
          expiresAt: 0,
          error: data?.message ?? '일회용 코드를 보내는데 실패했어요.',
        });
      }
    }
  }, [email]);

  const handleVerifyToken = useCallback(async () => {
    try {
      const response = await AccountApi.verifySignInVerificationTokenAndSignIn({
        challengeId: tokenState.challengeId,
        token,
      });

      await queryClient.invalidateQueries({ queryKey: [QueryKey.User], refetchType: 'all' });

      if (client.isWeb) {
        await fetchAccountMeQuery(queryClient);
        router.replace('/');
      } else {
        DirittoAppBridge.completeSign(response.token);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const data = (
          e as AxiosError<{
            message?: string;
          }>
        ).response?.data;

        setTokenState(ts => ({
          ...ts,
          error: data?.message ?? '일회용 코드를 확인하는데 실패했어요.',
        }));
      }
    }
  }, [token, tokenState.challengeId, router, queryClient, client]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = DateTime.now().toMillis();

      if (tokenState.expiresAt && tokenState.expiresAt < now) {
        setTokenRemainingTime('00:00');
        clearInterval(interval);
        setTokenState(ts => ({
          ...ts,
          expiresAt: 0,
          error: '인증시간이 지났습니다. 인증코드를 재전송해주세요.',
        }));
        return;
      }

      setTokenRemainingTime(
        tokenState.expiresAt ? Duration.fromMillis(tokenState.expiresAt - now).toFormat('mm:ss') : '05:00',
      );
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [tokenState.expiresAt, tokenState.challengeId]);

  return (
    <div className="container-center md:w-[500px] md:mt-[175px]">
      <div>
        <img
          src={`${configure.staticPrefix}/images/partner/partner-logo.svg`}
          alt="Contents Partners"
          className="w-[224px]"
        />
      </div>
      {tokenState.challengeId ? (
        <div className="container gap-[8px]">
          <input type="hidden" value={tokenState.challengeId} name="challengeId" />
          <div className="container gap-2 place-items-end">
            <input
              type="search"
              name="token"
              value={token}
              onChange={e => {
                setToken(e.target.value);
                setTokenState(ts => ({ ...ts, error: '' }));
              }}
              placeholder="일회용 코드를 입력해주세요."
              maxLength={6}
              className={`input ${tokenState?.error && 'border-red-500'}`}
            />
          </div>
          <div className="flex flex-row place-content-between">
            {tokenState.error ? <p className="text-red-500 text-xxxs">{tokenState.error}</p> : <div />}
            {tokenState.expiresAt > DateTime.now().toMillis() ? (
              <div className="text-primary-500 text-xxxs font-bold">{tokenRemainingTime}</div>
            ) : (
              <button
                type="button"
                onClick={handleSendToken}
                className="text-primary-500 text-xxxs font-bold underline">
                일회용 코드 재전송
              </button>
            )}
          </div>
          <button type="button" className="btn-primary" disabled={token.length !== 6} onClick={handleVerifyToken}>
            로그인
          </button>
        </div>
      ) : (
        <div className="container gap-[8px]">
          <input
            type="search"
            name="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setTokenState(ts => ({ ...ts, error: '' }));
            }}
            placeholder="이메일"
            className={`input text-xxs ${tokenState?.error && 'border-red-500'}`}
          />
          {tokenState.error && <p className="text-red-500 text-xxxs">{tokenState.error}</p>}
          <div className="container gap-2.5 mt-[8px]">
            <button type="button" className="btn-primary" onClick={handleSendToken}>
              일회용 코드로 로그인
            </button>
            <Link href="/accounts/partners/sign-up">
              <button className="btn-secondary-neutral w-full" type="button">
                파트너 계정 가입하기
              </button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
